import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import AutoGraphRoundedIcon from "@mui/icons-material/AutoGraphRounded";
import HubRoundedIcon from "@mui/icons-material/HubRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { useNavigate } from "react-router-dom";
import { adminApi } from "../../api/admin";

const BRAND = { orange: "#F35A3A", dark: "#19242F" };
const RANGE_OPTIONS = [7, 30, 90];

function inputDate(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

const BILLING_LABELS = {
  active: "Activas",
  trial: "Prueba",
  exempt: "Bonificadas",
  past_due: "Past due",
  pending: "Pendientes",
  paused: "Pausadas",
  cancelled: "Canceladas",
};

const MODULE_LABELS = {
  landing: "Landing",
  products: "Productos",
  orders: "Pedidos",
  payments: "Pagos",
  invoicing: "Gestión",
  stats: "Estadísticas",
  webpush: "Web Push",
  restaurant_menu: "Carta",
  memberships: "Membresías",
  recipes: "Recetas",
};

const PROFILE_LABELS = {
  commerce: "Comercio",
  services: "Servicios",
  gym: "Gimnasio",
  restaurant: "Restaurante",
  real_estate: "Inmobiliaria",
  "": "Sin perfil",
};

const CAPABILITY_LABELS = {
  memberships: "Membresías",
  gastronomicMenu: "Carta gastronómica",
  tables: "Mesas",
  tableReservations: "Reservas de mesa",
  kitchenOrders: "Comandas",
  recipes: "Recetas",
  properties: "Propiedades",
  leases: "Contratos",
  leaseCollections: "Cobranzas",
};

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("es-AR", { dateStyle: "medium", timeStyle: "short" }).format(date);
}

function formatMoney(amount, currency = "ARS") {
  try {
    return new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: currency || "ARS",
      maximumFractionDigits: 0,
    }).format(Number(amount || 0));
  } catch (_error) {
    return `${currency || ""} ${Number(amount || 0).toLocaleString("es-AR")}`.trim();
  }
}

function CurrencyValues({ rows = [], empty = "$0" }) {
  if (!rows.length) return <>{empty}</>;
  return (
    <Stack component="span" spacing={0.2}>
      {rows.map((row) => (
        <Typography component="span" key={row.currency} sx={{ fontSize: "inherit", fontWeight: "inherit", lineHeight: 1.1 }}>
          {formatMoney(row.amount, row.currency)}
        </Typography>
      ))}
    </Stack>
  );
}

function KpiCard({ icon, title, value, helper, onClick, tone = "default" }) {
  const content = (
    <CardContent sx={{ p: 2.3, "&:last-child": { pb: 2.3 } }}>
      <Stack direction="row" spacing={1.3} alignItems="flex-start">
        <Box sx={{ width: 42, height: 42, borderRadius: 2.5, display: "grid", placeItems: "center", bgcolor: tone === "warning" ? "rgba(237,108,2,.1)" : "rgba(243,90,58,.1)", color: tone === "warning" ? "warning.main" : BRAND.orange, flexShrink: 0 }}>
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: ".78rem", fontWeight: 850, color: "rgba(25,36,47,.62)" }}>{title}</Typography>
          <Typography component="div" sx={{ mt: .2, fontSize: { xs: "1.45rem", sm: "1.7rem" }, fontWeight: 950, color: BRAND.dark, lineHeight: 1.1 }}>{value}</Typography>
          {helper ? <Typography sx={{ mt: .7, fontSize: ".76rem", color: "rgba(25,36,47,.58)", lineHeight: 1.45 }}>{helper}</Typography> : null}
        </Box>
      </Stack>
    </CardContent>
  );
  return (
    <Card elevation={0} sx={{ height: "100%", border: "1px solid rgba(25,36,47,.08)", borderRadius: 3.2, minWidth: 0 }}>
      {onClick ? <CardActionArea onClick={onClick} sx={{ height: "100%", textAlign: "left" }}>{content}</CardActionArea> : content}
    </Card>
  );
}

function SectionCard({ title, subtitle, action, children }) {
  return (
    <Card elevation={0} sx={{ border: "1px solid rgba(25,36,47,.08)", borderRadius: 3.5, minWidth: 0, height: "100%" }}>
      <CardContent sx={{ p: { xs: 2, sm: 2.5 }, "&:last-child": { pb: { xs: 2, sm: 2.5 } } }}>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1.2} sx={{ mb: 2 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 950, color: BRAND.dark }}>{title}</Typography>
            {subtitle ? <Typography sx={{ mt: .3, fontSize: ".84rem", color: "rgba(25,36,47,.6)" }}>{subtitle}</Typography> : null}
          </Box>
          {action}
        </Stack>
        {children}
      </CardContent>
    </Card>
  );
}

function SmallDistribution({ rows = [], labelKey, valueKey = "count", maxRows = 7, onClick, labelMap = {} }) {
  const visible = rows.slice(0, maxRows);
  const max = Math.max(1, ...visible.map((row) => Number(row?.[valueKey] || 0)));
  if (!visible.length) return <Alert severity="info">Todavía no hay datos para esta distribución.</Alert>;
  return (
    <Stack spacing={1.2}>
      {visible.map((row) => {
        const key = row?.[labelKey] ?? "";
        const label = labelMap[key] || key || "Sin definir";
        const value = Number(row?.[valueKey] || 0);
        return (
          <Box key={`${key}-${value}`}>
            <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
              <Button disabled={!onClick} onClick={() => onClick?.(row)} sx={{ p: 0, minWidth: 0, justifyContent: "flex-start", textTransform: "none", color: BRAND.dark, fontWeight: 850 }}>{label}</Button>
              <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>{value}</Typography>
            </Stack>
            <LinearProgress variant="determinate" value={(value / max) * 100} sx={{ mt: .55, height: 6, borderRadius: 999, bgcolor: "rgba(25,36,47,.06)", "& .MuiLinearProgress-bar": { bgcolor: BRAND.orange, borderRadius: 999 } }} />
          </Box>
        );
      })}
    </Stack>
  );
}

export default function SuperPanelPage() {
  const navigate = useNavigate();
  const [rangeMode, setRangeMode] = React.useState("30");
  const [customFrom, setCustomFrom] = React.useState(() => inputDate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)));
  const [customTo, setCustomTo] = React.useState(() => inputDate(new Date()));
  const [summary, setSummary] = React.useState(null);
  const [adoption, setAdoption] = React.useState(null);
  const [integrations, setIntegrations] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [errors, setErrors] = React.useState({});

  const load = React.useCallback(async () => {
    setLoading(true);
    setErrors({});
    const params = rangeMode === "custom"
      ? { range: "custom", from: customFrom, to: customTo }
      : { days: Number(rangeMode) };
    const [summaryResult, adoptionResult, integrationsResult] = await Promise.allSettled([
      adminApi.commandCenterSummary(params),
      adminApi.commandCenterAdoption(params),
      adminApi.commandCenterIntegrations({ page: 1, limit: 8 }),
    ]);
    if (summaryResult.status === "fulfilled") setSummary(summaryResult.value.summary);
    else setErrors((previous) => ({ ...previous, summary: summaryResult.reason?.message || "No se pudo cargar el resumen" }));
    if (adoptionResult.status === "fulfilled") setAdoption(adoptionResult.value.adoption);
    else setErrors((previous) => ({ ...previous, adoption: adoptionResult.reason?.message || "No se pudo cargar adopción" }));
    if (integrationsResult.status === "fulfilled") setIntegrations(integrationsResult.value.integrations);
    else setErrors((previous) => ({ ...previous, integrations: integrationsResult.reason?.message || "No se pudieron cargar integraciones" }));
    setLoading(false);
  }, [customFrom, customTo, rangeMode]);

  React.useEffect(() => { load(); }, [load]);

  const projectList = (params = {}) => {
    const search = new URLSearchParams();
    const rangeParams = rangeMode === "custom"
      ? { range: "custom", from: customFrom, to: customTo }
      : { days: String(rangeMode) };
    Object.entries({ ...rangeParams, ...params }).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") search.set(key, value);
    });
    navigate(`/dashboard/superpanel/proyectos${search.toString() ? `?${search}` : ""}`);
  };

  const project360 = (slug) => {
    const search = new URLSearchParams();
    if (rangeMode === "custom") {
      search.set("range", "custom");
      search.set("from", customFrom);
      search.set("to", customTo);
    } else {
      search.set("days", String(rangeMode));
    }
    navigate(`/dashboard/superpanel/proyectos/${slug}${search.toString() ? `?${search}` : ""}`);
  };

  const activityList = (params = {}) => {
    const search = new URLSearchParams();
    const from = inputDate(summary?.range?.from);
    const to = inputDate(summary?.range?.to);
    if (from) search.set("from", from);
    if (to) search.set("to", to);
    Object.entries(params).forEach(([key, value]) => { if (value) search.set(key, value); });
    navigate(`/dashboard/actividad${search.toString() ? `?${search}` : ""}`);
  };

  const recurringCount = (summary?.billing?.recurringActive || []).reduce((sum, row) => sum + Number(row.count || 0), 0);
  const riskCount = (summary?.billing?.recurringAtRisk || []).reduce((sum, row) => sum + Number(row.count || 0), 0);

  return (
    <Stack spacing={2.5} sx={{ minWidth: 0 }}>
      <Stack direction={{ xs: "column", lg: "row" }} justifyContent="space-between" spacing={1.5} alignItems={{ lg: "flex-start" }}>
        <Box>
          <Typography variant="h3" sx={{ color: BRAND.dark, fontWeight: 950, fontSize: { xs: "1.9rem", sm: "2.5rem", md: "3rem" }, letterSpacing: "-1px" }}>Super Panel</Typography>
          <Typography sx={{ mt: .45, color: "rgba(25,36,47,.64)", maxWidth: 760 }}>Centro de mando de WebProfit: una cifra siempre debe poder llevarte a los proyectos que la explican.</Typography>
          <Typography sx={{ mt: .5, fontSize: ".78rem", color: "rgba(25,36,47,.5)" }}>Última actualización: {formatDate(summary?.generatedAt)}</Typography>
        </Box>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ width: { xs: "100%", lg: "auto" } }}>
          <TextField select size="small" label="Período" value={rangeMode} onChange={(event) => setRangeMode(String(event.target.value))} sx={{ minWidth: 170 }}>
            {RANGE_OPTIONS.map((value) => <MenuItem key={value} value={String(value)}>Últimos {value} días</MenuItem>)}
            <MenuItem value="custom">Rango personalizado</MenuItem>
          </TextField>
          {rangeMode === "custom" ? (
            <>
              <TextField size="small" type="date" label="Desde" value={customFrom} onChange={(event) => setCustomFrom(event.target.value)} InputLabelProps={{ shrink: true }} />
              <TextField size="small" type="date" label="Hasta" value={customTo} onChange={(event) => setCustomTo(event.target.value)} InputLabelProps={{ shrink: true }} />
            </>
          ) : null}
          <Button variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={load} disabled={loading} sx={{ textTransform: "none", fontWeight: 900 }}>Actualizar</Button>
          <Button variant="contained" onClick={() => projectList()} sx={{ bgcolor: BRAND.orange, textTransform: "none", fontWeight: 900 }}>Explorar negocios</Button>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        <Button onClick={() => navigate("/dashboard/usuarios")} variant="outlined" sx={{ textTransform: "none", fontWeight: 850 }}>Usuarios</Button>
        <Button onClick={() => navigate("/dashboard/billing")} variant="outlined" sx={{ textTransform: "none", fontWeight: 850 }}>Planes y suscripciones</Button>
        <Button onClick={() => navigate("/dashboard/actividad")} variant="outlined" sx={{ textTransform: "none", fontWeight: 850 }}>Actividad global</Button>
        <Button onClick={() => navigate("/dashboard/analitica")} variant="outlined" sx={{ textTransform: "none", fontWeight: 850 }}>Analítica</Button>
        <Button onClick={() => navigate("/dashboard/superpanel/integraciones")} variant="outlined" sx={{ textTransform: "none", fontWeight: 850 }}>Integraciones</Button>
      </Stack>

      {errors.summary ? <Alert severity="error" action={<Button color="inherit" onClick={load}>Reintentar</Button>}>{errors.summary}</Alert> : null}

      <Box>
        <Typography sx={{ mb: 1, fontWeight: 950, color: BRAND.dark }}>Ahora</Typography>
        <Grid container spacing={1.5}>
          {loading && !summary ? Array.from({ length: 6 }).map((_, index) => <Grid item xs={6} md={4} lg={2} key={index}><Skeleton variant="rounded" height={150} /></Grid>) : (
            <>
              <Grid item xs={6} md={4} lg={2}><KpiCard icon={<StorefrontRoundedIcon />} title="Negocios totales" value={summary?.projects?.total ?? 0} helper="Todos los proyectos, sin límites de muestra." onClick={() => projectList()} /></Grid>
              <Grid item xs={6} md={4} lg={2}><KpiCard icon={<StorefrontRoundedIcon />} title="Negocios activos" value={summary?.projects?.active ?? 0} helper="Project.status = active." onClick={() => projectList({ status: "active" })} /></Grid>
              <Grid item xs={6} md={4} lg={2}><KpiCard icon={<PaymentsRoundedIcon />} title="Recurrentes activas" value={recurringCount} helper="Billing recurrente + active." onClick={() => projectList({ billingStatus: "active", paymentMode: "recurring" })} /></Grid>
              <Grid item xs={6} md={4} lg={2}><KpiCard icon={<AutoGraphRoundedIcon />} title="Valor mensual activo" value={<CurrencyValues rows={summary?.billing?.recurringActive || []} />} helper="Valor contractual actual, no caja cobrada." onClick={() => projectList({ billingStatus: "active", paymentMode: "recurring" })} /></Grid>
              <Grid item xs={6} md={4} lg={2}><KpiCard icon={<WarningAmberRoundedIcon />} title="Valor mensual en riesgo" value={<CurrencyValues rows={summary?.billing?.recurringAtRisk || []} />} helper={`${riskCount} recurrente${riskCount === 1 ? "" : "s"} past_due.`} tone="warning" onClick={() => projectList({ billingStatus: "past_due", paymentMode: "recurring" })} /></Grid>
              <Grid item xs={6} md={4} lg={2}><KpiCard icon={<WarningAmberRoundedIcon />} title="Problemas operativos" value={summary?.projects?.operationalProblems ?? 0} helper={summary?.signals?.integrityCoverage === "partial" ? "Señales conocidas; la última auditoría almacenó un listado parcial." : "Deployment, dominios, auditoría, integración o actividad crítica."} tone="warning" onClick={() => projectList({ health: "attention" })} /></Grid>
            </>
          )}
        </Grid>
      </Box>

      <Box>
        <Typography sx={{ mb: 1, fontWeight: 950, color: BRAND.dark }}>{summary?.range?.label || (rangeMode === "custom" ? "Rango personalizado" : `Últimos ${rangeMode} días`)}</Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={6} md={4} lg={2}><KpiCard icon={<StorefrontRoundedIcon />} title="Nuevos negocios" value={summary?.projects?.newInRange ?? 0} helper={`Período anterior comparable: ${summary?.projects?.previousNewInRange ?? 0}.`} onClick={() => projectList({ createdFrom: inputDate(summary?.range?.from), createdTo: inputDate(summary?.range?.to) })} /></Grid>
          <Grid item xs={6} md={4} lg={2}><KpiCard icon={<PeopleAltRoundedIcon />} title="Nuevos usuarios" value={summary?.users?.newInRange ?? 0} helper={`${summary?.users?.total ?? 0} usuarios totales · ${summary?.users?.pending ?? 0} pendientes hoy.`} onClick={() => navigate(`/dashboard/usuarios?createdFrom=${encodeURIComponent(inputDate(summary?.range?.from))}&createdTo=${encodeURIComponent(inputDate(summary?.range?.to))}`)} /></Grid>
          <Grid item xs={6} md={4} lg={2}><KpiCard icon={<PaymentsRoundedIcon />} title="Cobros por período" value={<CurrencyValues rows={summary?.payments?.approvedPeriodOrManual || []} />} helper="PlanCheckout approved del rango completo." onClick={() => projectList({ commercialEvent: "approved_payment" })} /></Grid>
          <Grid item xs={6} md={4} lg={2}><KpiCard icon={<AutoGraphRoundedIcon />} title="Altas comerciales" value={summary?.billing?.observedCommercialEvents?.firstActivations ?? 0} helper="Primera autorización observable por proyecto." onClick={() => projectList({ commercialEvent: "first_activation" })} /></Grid>
          <Grid item xs={6} md={4} lg={2}><KpiCard icon={<AutoGraphRoundedIcon />} title="Cambios de servicios" value={summary?.billing?.observedCommercialEvents?.serviceChanges ?? 0} helper="Proyectos con autorización y selección realmente distinta." onClick={() => projectList({ commercialEvent: "service_change" })} /></Grid>
          <Grid item xs={6} md={4} lg={2}><KpiCard icon={<WarningAmberRoundedIcon />} title="Actividad warning/critical" value={summary?.activity?.warningOrCritical ?? 0} helper="Eventos observados en el período seleccionado." tone="warning" onClick={() => activityList({ severity: "warning,critical" })} /></Grid>
        </Grid>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} lg={6}>
          <SectionCard title="Situación comercial" subtitle="Estado actual del billing vigente; no confunde proyecto activo con suscripción activa." action={<Button onClick={() => navigate("/dashboard/billing")} endIcon={<ArrowForwardRoundedIcon />} sx={{ textTransform: "none", fontWeight: 900 }}>Administrar billing</Button>}>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
              {(summary?.billing?.byStatus || []).map((row) => <Chip key={row.status} clickable onClick={() => projectList({ billingStatus: row.status })} label={`${BILLING_LABELS[row.status] || row.status}: ${row.count}`} />)}
              <Chip clickable color="info" variant="outlined" onClick={() => projectList({ customPricing: "1" })} label={`Precios particulares: ${summary?.billing?.customPricingProjects ?? 0}`} />
            </Stack>
            <Divider sx={{ mb: 2 }} />
            <Typography sx={{ mb: 1.3, fontWeight: 900, color: BRAND.dark }}>Concentración por servicio</Typography>
            <SmallDistribution rows={summary?.billing?.services || []} labelKey="code" valueKey="projects" onClick={(row) => projectList({ service: row.code })} />
            <Stack spacing={.75} sx={{ mt: 1.5 }}>
              {(summary?.billing?.services || []).slice(0, 5).map((row) => (
                <Stack key={`value-${row.code}`} direction="row" justifyContent="space-between" spacing={1} alignItems="center">
                  <Typography sx={{ fontSize: ".76rem", color: "rgba(25,36,47,.58)" }}>{row.code} · valor mensual por snapshot ({row.monthlyValueCoverage})</Typography>
                  <Typography component="div" sx={{ fontSize: ".78rem", fontWeight: 900, color: BRAND.dark }}><CurrencyValues rows={row.monthlyValueByCurrency || []} empty="Sin snapshot" /></Typography>
                </Stack>
              ))}
            </Stack>
            <Divider sx={{ my: 2 }} />
            <Typography sx={{ mb: 1.2, fontWeight: 900, color: BRAND.dark }}>Mayor valor mensual recurrente por cliente</Typography>
            <Stack spacing={1.2}>
              {(summary?.billing?.topRecurringByCurrency || []).map((group) => (
                <Box key={group.currency}>
                  <Typography sx={{ mb: .55, fontSize: ".74rem", fontWeight: 900, color: "rgba(25,36,47,.5)" }}>{group.currency}</Typography>
                  <Stack spacing={.7}>
                    {(group.projects || []).map((row) => (
                      <Button key={row.id} onClick={() => project360(row.slug)} sx={{ p: 1, px: 1.2, borderRadius: 2.3, bgcolor: "#F6F7FB", justifyContent: "space-between", textTransform: "none", color: BRAND.dark, fontWeight: 850 }}>
                        <span>{row.name}</span><strong>{formatMoney(row.monthlyAmount, group.currency)}</strong>
                      </Button>
                    ))}
                  </Stack>
                </Box>
              ))}
              {!(summary?.billing?.topRecurringByCurrency || []).length ? <Typography sx={{ fontSize: ".82rem", color: "rgba(25,36,47,.56)" }}>Todavía no hay recurrentes activas para concentrar.</Typography> : null}
            </Stack>
          </SectionCard>
        </Grid>

        <Grid item xs={12} lg={6}>
          <SectionCard title="Uso del producto" subtitle="Habilitado y uso observado son conceptos distintos." action={<Button onClick={() => navigate("/dashboard/analitica")} endIcon={<ArrowForwardRoundedIcon />} sx={{ textTransform: "none", fontWeight: 900 }}>Ver analítica</Button>}>
            {errors.adoption ? <Alert severity="warning">{errors.adoption}</Alert> : null}
            {!errors.adoption && !adoption ? <Skeleton variant="rounded" height={210} /> : null}
            <Stack spacing={1.4}>
              {(adoption?.items || []).map((item) => (
                <Box key={item.key}>
                  <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
                    <Button onClick={() => projectList(item.filter || {})} sx={{ p: 0, minWidth: 0, textTransform: "none", color: BRAND.dark, fontWeight: 900 }}>{item.label}</Button>
                    {item.coverage === "insufficient" ? (
                      <Chip size="small" label={`${item.enabled} habilitados · sin cobertura suficiente`} variant="outlined" />
                    ) : (
                      <Button
                        onClick={() => projectList({ ...(item.filter || {}), usageObserved: item.key })}
                        sx={{ p: 0, minWidth: 0, textTransform: "none", fontSize: ".82rem", color: "rgba(25,36,47,.62)", fontWeight: 850 }}
                      >
                        {item.observed}/{item.enabled} observados · {item.observedPercent}%
                      </Button>
                    )}
                  </Stack>
                  {item.coverage === "instrumented" ? <LinearProgress variant="determinate" value={item.observedPercent || 0} sx={{ mt: .55, height: 6, borderRadius: 99 }} /> : null}
                </Box>
              ))}
            </Stack>
            {adoption?.note ? <Typography sx={{ mt: 1.6, fontSize: ".76rem", color: "rgba(25,36,47,.54)" }}>{adoption.note}</Typography> : null}
          </SectionCard>
        </Grid>

        <Grid item xs={12} lg={6}>
          <SectionCard title="Integraciones" subtitle="Estado seguro por proveedor, sin tokens ni secretos." action={<Button onClick={() => navigate("/dashboard/superpanel/integraciones")} endIcon={<ArrowForwardRoundedIcon />} sx={{ textTransform: "none", fontWeight: 900 }}>Ver todas</Button>}>
            {errors.integrations ? <Alert severity="warning">{errors.integrations}</Alert> : null}
            {!errors.integrations && !(integrations?.items || []).length ? <Alert severity="info">Todavía no hay integraciones configuradas.</Alert> : null}
            <Stack direction="row" spacing={.7} useFlexGap flexWrap="wrap" sx={{ mb: 1.2 }}>
              {(summary?.integrations || []).map((row, index) => (
                <Chip
                  key={`${row.provider}-${row.enabled}-${row.connected}-${row.hasError}-${index}`}
                  clickable
                  onClick={() => navigate(`/dashboard/superpanel/integraciones?provider=${encodeURIComponent(row.provider)}${row.hasError ? "&status=error" : row.enabled && row.connected ? "&status=connected" : row.enabled ? "&status=incomplete" : "&status=disabled"}`)}
                  size="small"
                  label={`${row.provider}: ${row.count}`}
                  color={row.hasError ? "error" : row.enabled && row.connected ? "success" : row.enabled ? "warning" : "default"}
                  variant="outlined"
                />
              ))}
            </Stack>
            <Stack spacing={1}>
              {(integrations?.items || []).slice(0, 6).map((item) => (
                <Box key={item.id} sx={{ p: 1.2, borderRadius: 2.5, bgcolor: "#F6F7FB" }}>
                  <Stack direction="row" justifyContent="space-between" spacing={1}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>{item.project?.name || "Proyecto"}</Typography>
                      <Typography sx={{ fontSize: ".76rem", color: "rgba(25,36,47,.58)" }}>{item.provider} · {item.mode}</Typography>
                      <Typography sx={{ mt: .25, fontSize: ".7rem", color: "rgba(25,36,47,.5)" }}>Prueba: {formatDate(item.lastTestedAt)} · Sync OK: {formatDate(item.lastSuccessfulSyncAt)}</Typography>
                    </Box>
                    <Chip size="small" label={item.state} color={item.state === "connected" ? "success" : item.state === "error" ? "error" : "warning"} />
                  </Stack>
                  {item.lastError ? <Typography sx={{ mt: .65, fontSize: ".73rem", color: "error.main" }}>{item.lastError}</Typography> : null}
                </Box>
              ))}
            </Stack>
          </SectionCard>
        </Grid>

        <Grid item xs={12} lg={6}>
          <SectionCard title="Actividad reciente" subtitle={`${summary?.activity?.warningOrCritical ?? 0} evento(s) warning/critical en el período.`} action={<Button onClick={() => activityList()} endIcon={<ArrowForwardRoundedIcon />} sx={{ textTransform: "none", fontWeight: 900 }}>Ver toda</Button>}>
            <Stack spacing={1}>
              {(summary?.activity?.recent || []).slice(0, 7).map((event) => (
                <Box key={event.id} sx={{ p: 1.2, borderRadius: 2.5, bgcolor: "#F6F7FB" }}>
                  <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 900, color: BRAND.dark, overflowWrap: "anywhere" }}>{event.title}</Typography>
                      <Typography sx={{ mt: .2, fontSize: ".75rem", color: "rgba(25,36,47,.56)" }}>{event.projectName || event.projectSlug || "Global"} · {formatDate(event.occurredAt)}</Typography>
                    </Box>
                    <Chip size="small" label={event.severity || "info"} color={event.severity === "critical" ? "error" : event.severity === "warning" ? "warning" : "default"} />
                  </Stack>
                </Box>
              ))}
              {!loading && !(summary?.activity?.recent || []).length ? <Alert severity="info">No hay actividad registrada en este período.</Alert> : null}
            </Stack>
          </SectionCard>
        </Grid>
      </Grid>

      <SectionCard title="Señales operativas" subtitle="Hallazgos accionables de la última auditoría de integridad. No son tickets artificiales." action={<Button onClick={() => projectList({ health: "attention" })} endIcon={<ArrowForwardRoundedIcon />} sx={{ textTransform: "none", fontWeight: 900 }}>Investigar proyectos</Button>}>
        <Stack spacing={1}>
          {(summary?.signals?.integrityFindings || []).length ? (summary.signals.integrityFindings.map((finding) => (
            <Box key={finding.id} sx={{ p: 1.25, borderRadius: 2.5, bgcolor: "#F6F7FB" }}>
              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1}>
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={.7} useFlexGap flexWrap="wrap"><Chip size="small" label={finding.severity} color={["critical", "high"].includes(finding.severity) ? "error" : finding.severity === "medium" ? "warning" : "default"} /><Chip size="small" variant="outlined" label={finding.category || "project"} /></Stack>
                  <Typography sx={{ mt: .65, fontWeight: 900, color: BRAND.dark }}>{finding.message}</Typography>
                  <Typography sx={{ mt: .25, fontSize: ".76rem", color: "rgba(25,36,47,.56)" }}>{finding.cause}</Typography>
                </Box>
                {finding.projectSlug ? <Button onClick={() => project360(finding.projectSlug)} sx={{ alignSelf: { xs: "stretch", sm: "center" }, textTransform: "none", fontWeight: 900 }}>Ver proyecto</Button> : null}
              </Stack>
            </Box>
          ))) : summary?.signals?.integrityCoverage === "unavailable" ? <Alert severity="info">Todavía no hay una auditoría de integridad finalizada para usar como señal.</Alert> : <Alert severity="success">La última auditoría no almacenó hallazgos accionables para mostrar.</Alert>}
          {summary?.signals?.integrityCoverage === "partial" ? <Alert severity="info">La auditoría indica que el listado almacenado es parcial. El Super Panel no presenta esta muestra como una cobertura total.</Alert> : null}
        </Stack>
      </SectionCard>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <SectionCard title="Negocios por perfil" subtitle="Distribución por businessProfile; los proyectos legacy quedan como Sin perfil.">
            <SmallDistribution rows={summary?.profiles || []} labelKey="profile" valueKey="count" labelMap={PROFILE_LABELS} onClick={(row) => projectList(row.profile ? { businessProfile: row.profile } : { businessProfileMissing: "1" })} />
          </SectionCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <SectionCard title="Funcionalidades habilitadas" subtitle="Fotografía de módulos y capabilities, no una afirmación de uso.">
            <Grid container spacing={1.2}>
              {(summary?.enabled?.modules || []).slice(0, 8).map((row) => <Grid item xs={6} key={`m-${row.key}`}><Button fullWidth onClick={() => projectList({ module: row.key })} sx={{ justifyContent: "space-between", textTransform: "none", bgcolor: "#F6F7FB", color: BRAND.dark, fontWeight: 850, borderRadius: 2.4, p: 1.2 }}><span>{MODULE_LABELS[row.key] || row.key}</span><strong>{row.count}</strong></Button></Grid>)}
              {(summary?.enabled?.capabilities || []).slice(0, 6).map((row) => <Grid item xs={6} key={`c-${row.key}`}><Button fullWidth onClick={() => projectList({ capability: row.key })} sx={{ justifyContent: "space-between", textTransform: "none", bgcolor: "rgba(243,90,58,.06)", color: BRAND.dark, fontWeight: 850, borderRadius: 2.4, p: 1.2 }}><span>{CAPABILITY_LABELS[row.key] || row.key}</span><strong>{row.count}</strong></Button></Grid>)}
            </Grid>
          </SectionCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <SectionCard title="Cobertura económica" subtitle="Qué se puede medir hoy sin inventar semántica.">
            <Stack spacing={1}>
              <Alert severity="success">Valor mensual recurrente actual: disponible por moneda.</Alert>
              <Alert severity="success">Cobros por período/manuales aprobados: disponibles para el rango completo.</Alert>
              <Alert severity="info">Bajas históricas: {summary?.billing?.observedCommercialEvents?.cancellationsCoverage || "sin cobertura suficiente"}</Alert>
              <Alert severity="warning">MRR histórico, churn, LTV y ARPU no se muestran porque falta un ledger económico canónico por cobro/transición.</Alert>
            </Stack>
          </SectionCard>
        </Grid>
      </Grid>

      <Card elevation={0} sx={{ borderRadius: 3.5, border: "1px solid rgba(25,36,47,.08)" }}>
        <CardContent sx={{ p: 2.2, "&:last-child": { pb: 2.2 } }}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1.5} alignItems={{ md: "center" }}>
            <Stack direction="row" spacing={1.2} alignItems="center">
              <HubRoundedIcon sx={{ color: BRAND.orange }} />
              <Box>
                <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>Investigar, no sólo observar</Typography>
                <Typography sx={{ fontSize: ".82rem", color: "rgba(25,36,47,.6)" }}>Abrí el explorador global para buscar, filtrar y llegar a la ficha 360° de cada negocio.</Typography>
              </Box>
            </Stack>
            <Button variant="contained" onClick={() => projectList()} endIcon={<ArrowForwardRoundedIcon />} sx={{ bgcolor: BRAND.orange, textTransform: "none", fontWeight: 900 }}>Explorar todos los negocios</Button>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}
